import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  DestroyRef,
  inject,
  ViewChild,
  signal,
  computed,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// FullCalendar Core and Plugins
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventClickArg, EventInput, EventContentArg, DatesSetArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';

import { OrderInfoModalComponent } from '../../widgets/orders/order-info-modal/order-info-modal.component';
import { InstallationService } from '../../../services/orders/installation.service';
import { CalendarViewSelectComponent } from './components/calendar-view-select/calendar-view-select.component';
import { Router } from '@angular/router';
import { DeviceService } from '../../../services/device/device.service';

/**
 * Maps UCO order status to a hex colour consistent with the dot classes
 * defined in management-order.component.scss:
 *   .dot-complete    → #198754
 *   .dot-no-complete → #dc3545
 *   .dot-warning     → #f59e0d
 *   .dot-unassigned  → #6c757d
 */
function resolveStatusColor(order: any): string {
  const now       = new Date();
  const startDate = new Date(order.start_date);

  // 1. Workflow states — maximum priority, independent of date
  if (order.status === 'PARTIAL')  return '#f97316'; // orange — Partial
  if (order.status === 'CLOSE')    return '#198754'; // green  — Completed
  if (order.status === 'Assigned') return '#7c3aed'; // purple — Assigned

  // 2. Temporal semaphore — only for unassigned orders (New / Unassigned)
  if (sameDay(startDate, now))                       return '#f59e0d'; // amber  — Today
  if (startDate.getTime() < now.getTime())           return '#dc3545'; // red    — Due
  return '#0ea5e9';                                                     // blue   — On Time
}

function formatKey(d: Date): string {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function sameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth()    === d2.getMonth()    &&
         d1.getDate()     === d2.getDate();
}

@Component({
    selector: 'app-orders-calendar',
    imports: [CommonModule, FullCalendarModule, CalendarViewSelectComponent],
    templateUrl: './orders-calendar.component.html',
    styleUrls: ['./orders-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrdersCalendarComponent implements OnInit, OnChanges {
  private readonly modalService        = inject(NgbModal);
  private readonly installationService = inject(InstallationService);
  private readonly destroyRef          = inject(DestroyRef);
  private readonly router              = inject(Router);
  private readonly deviceService        = inject(DeviceService);

  @Input() statusFilter: string = '';

  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  /** Orders fetched for the currently visible date range. */
  calendarOrders = signal<any[]>([]);
  isLoading      = signal(false);

  /** Active FullCalendar / custom-grid view name. */
  currentView = 'dayGridMonth';

  // ─── Custom monthly-grid state ────────────────────────────────────────────
  readonly weekdays    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly currentDate = signal<Date>(new Date());
  readonly calendarTitle = signal<string>('');

  readonly currentMonthLabel = computed(() => {
    return this.currentDate().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  });

  readonly currentTitle = computed<string>(() =>
    this.currentView === 'dayGridMonth' ? this.currentMonthLabel() : this.calendarTitle()
  );

  /** Orders grouped by YYYY-MM-DD key (UCO field: `start_date`). */
  readonly ordersByDate = computed(() => {
    const map: Record<string, any[]> = {};
    this.calendarOrders().forEach(order => {
      if (order.start_date) {
        const key = formatKey(new Date(order.start_date));
        if (!map[key]) map[key] = [];
        map[key].push(order);
      }
    });
    return map;
  });

  /** 42-cell calendar grid (prev + current + next month days). */
  readonly calendarDays = computed(() => {
    const date  = this.currentDate();
    const year  = date.getFullYear();
    const month = date.getMonth();

    const firstDay      = new Date(year, month, 1);
    const startWeekday  = firstDay.getDay();
    const totalDays     = new Date(year, month + 1, 0).getDate();
    const prevTotalDays = new Date(year, month, 0).getDate();
    const today         = new Date();
    const ordersMap     = this.ordersByDate();

    type Day = { date: Date; dayNumber: number; isCurrentMonth: boolean; isToday: boolean; orders: any[] };
    const days: Day[] = [];

    // Previous month tail
    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevTotalDays - i);
      days.push({ date: d, dayNumber: d.getDate(), isCurrentMonth: false, isToday: sameDay(d, today), orders: ordersMap[formatKey(d)] || [] });
    }
    // Current month
    for (let i = 1; i <= totalDays; i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, dayNumber: i, isCurrentMonth: true, isToday: sameDay(d, today), orders: ordersMap[formatKey(d)] || [] });
    }
    // Next month head (fill to 42 cells)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: d, dayNumber: i, isCurrentMonth: false, isToday: sameDay(d, today), orders: ordersMap[formatKey(d)] || [] });
    }

    return days;
  });

  // ─── FullCalendar config (week / day / list views only) ───────────────────
  calendarOptions: CalendarOptions = {
    /**
     * Header toolbar is disabled — navigation is handled by the custom
     * calendar-nav bar rendered in the component HTML.
     */
    headerToolbar: false,
    /**
     * Month view is the custom HTML grid above.
     * FullCalendar is rendered only for timeGridWeek / timeGridDay / listWeek.
     */
    initialView: 'timeGridWeek',
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    weekends: true,
    navLinks: true,
    dayMaxEvents: true,
    buttonIcons: false,
    eventClick: this.handleEventClick.bind(this),
    datesSet: this.onDatesSet.bind(this),
    events: [],
    height: 'auto',
    eventDisplay: 'auto',
    eventTimeFormat: { hour: 'numeric', minute: '2-digit', meridiem: 'short' },
    eventContent: this.renderEventContent.bind(this)
  };

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.currentView === 'dayGridMonth') {
      this.loadCustomMonth();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['statusFilter'] && !changes['statusFilter'].firstChange) {
      this.reloadEvents();
    }
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  prev(): void {
    if (this.currentView === 'dayGridMonth') {
      const d = this.currentDate();
      this.currentDate.set(new Date(d.getFullYear(), d.getMonth() - 1, 1));
      this.loadCustomMonth();
    } else if (this.calendarComponent) {
      this.calendarComponent.getApi().prev();
    }
  }

  next(): void {
    if (this.currentView === 'dayGridMonth') {
      const d = this.currentDate();
      this.currentDate.set(new Date(d.getFullYear(), d.getMonth() + 1, 1));
      this.loadCustomMonth();
    } else if (this.calendarComponent) {
      this.calendarComponent.getApi().next();
    }
  }

  // ─── View switching ───────────────────────────────────────────────────────

  onViewChange(viewName: string): void {
    this.currentView = viewName;
    if (viewName !== 'dayGridMonth') {
      this.calendarOptions = { ...this.calendarOptions, initialView: viewName };
      setTimeout(() => {
        if (this.calendarComponent) {
          const api = this.calendarComponent.getApi();
          api.changeView(viewName);
          this.fetchOrders(api.view.currentStart, api.view.currentEnd);
        }
      }, 0);
    } else {
      this.loadCustomMonth();
    }
  }

  /** Called by FullCalendar when the visible range changes (prev/next inside FC). */
  onDatesSet(dateInfo: DatesSetArg): void {
    if (this.currentView !== 'dayGridMonth') {
      this.calendarTitle.set(dateInfo.view.title);
      this.fetchOrders(dateInfo.start, dateInfo.end);
    }
  }

  // ─── Data loading ─────────────────────────────────────────────────────────

  loadCustomMonth(): void {
    const days = this.calendarDays();
    if (days.length === 0) return;
    this.fetchOrders(days[0].date, days[days.length - 1].date);
  }

  reloadEvents(): void {
    if (this.currentView === 'dayGridMonth') {
      this.loadCustomMonth();
    } else if (this.calendarComponent) {
      const api = this.calendarComponent.getApi();
      this.fetchOrders(api.view.currentStart, api.view.currentEnd);
    }
  }

  fetchOrders(start: Date, end: Date): void {
    this.isLoading.set(true);
    this.installationService.getAllOrders(1, 1000, start, end, '', this.statusFilter || undefined, 'start_date')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const orders: any[] = res?.orders ?? res ?? [];
          this.calendarOrders.set(orders);

          // For FullCalendar views, push events via the API
          if (this.currentView !== 'dayGridMonth') {
            const api = this.calendarComponent?.getApi();
            if (api) {
              api.removeAllEvents();
              api.addEventSource(orders.map(o => this.mapOrderToEvent(o)));
            }
          }
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }

  // ─── FullCalendar event mapping ───────────────────────────────────────────

  private mapOrderToEvent(order: any): EventInput {
    const start = order.start_date || new Date().toISOString();
    const title = order.name_client_address || '—';
    const color = resolveStatusColor(order);
    return {
      id: order.ident_order,
      title,
      start,
      color,
      textColor: '#ffffff',
      extendedProps: { order }
    };
  }

  /** Event click handler for FullCalendar (non-month views). */
  handleEventClick(clickInfo: EventClickArg): void {
    const order = clickInfo.event.extendedProps['order'];
    if (!order) return;
    this.openDetail(order);
  }

  /**
   * Opens the OrderInfoModal for a given order.
   * Used by both the HTML grid (month view) and FC event click handler.
   */
  openDetail(order: any, event?: Event): void {
    if (event) event.stopPropagation();

    // ── Tablet / Service Technician: navigate to full-screen page ──
    if (this.deviceService.isTablet()) {
      this.router.navigate(['/uco/manage-order/edit', order._id]);
      return;
    }

    // ── Desktop: keep existing modal flow ──
    const modalRef = this.modalService.open(OrderInfoModalComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });

    const ref = modalRef as any;
    const componentRef = ref.componentRef || ref._contentRef?.componentRef;

    if (componentRef) {
      componentRef.setInput('ident_order', order._id);
    } else {
      modalRef.componentInstance.ident_order = order._id;
    }

    modalRef.result.then((result) => {
      if (result?.action === 'refresh') {
        this.reloadEvents();
      }
    }).catch(() => {});
  }

  // ─── FullCalendar event content renderer ──────────────────────────────────

  renderEventContent(eventInfo: EventContentArg) {
    const order = eventInfo.event.extendedProps['order'];
    if (!order) return { html: eventInfo.event.title };

    const color    = resolveStatusColor(order);
    const timeHtml = eventInfo.timeText
      ? `<span class="fc-event-time" style="font-weight:700;margin-right:3px;font-size:0.9rem;">${eventInfo.timeText}</span>`
      : '';

    return {
      html: `
        <div class="status-badge-container" style="display:flex;align-items:center;gap:4px;overflow:hidden;padding:1px 4px;">
          <span class="status-dot" style="flex-shrink:0;display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${color};"></span>
          ${timeHtml}
          <span class="status-text" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:0.82rem;font-weight:500;color:#334155;">
            ${eventInfo.event.title}
          </span>
        </div>
      `
    };
  }

  // ─── Helpers exposed to template ──────────────────────────────────────────

  /** Returns the hex status colour for a UCO order (used in the HTML grid). */
  getStatusColor(order: any): string {
    return resolveStatusColor(order);
  }
}

